import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { Profile } from '../lib/supabase';

interface Transaction {
  id: string;
  amount: number;
  currency: string;
  payment_type: 'credit' | 'debit';
  remarks: string;
  created_at: string;
  vendor_smart_key: string;
  customer_smart_key: string;
  vendor?: Profile;
  customer?: Profile;
}

interface CustomerProfile extends Profile {
  balance: number;
}

type TimeframeType = 'All' | 'Year' | 'Month' | 'Week' | 'Day';

function PrintableStatement() {
  const navigate = useNavigate();
  const { id: profileSmartKey } = useParams();
  const [timeframe, setTimeframe] = useState<TimeframeType>('Month');
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [currentUser, setCurrentUser] = useState<Profile | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalBalance, setTotalBalance] = useState(0);

  useEffect(() => {
    if (!profileSmartKey) {
      setError('Invalid profile ID');
      setLoading(false);
      navigate('/home');
      return;
    }
    fetchCurrentUser();
  }, [profileSmartKey, navigate]);

  useEffect(() => {
    if (currentUser && profile) {
      fetchTotalBalance();
      fetchTransactions(currentUser);
    }
  }, [currentUser, profile, timeframe]);

  const fetchCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login', { replace: true });
        return;
      }

      const { data: userProfile, error: userError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;
      if (!userProfile) throw new Error('لم يتم العثور على الملف الشخصي');

      setCurrentUser(userProfile);
      fetchProfileDetails(userProfile);
    } catch (err) {
      console.error('Error fetching user:', err);
      setError('فشل في تحميل بيانات المستخدم');
      setLoading(false);
    }
  };

  const fetchProfileDetails = async (currentUser: Profile) => {
    try {
      if (!profileSmartKey) {
        throw new Error('Invalid profile ID');
      }

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('smart_key', profileSmartKey)
        .single();

      if (profileError) throw profileError;
      if (!profileData) throw new Error('Profile not found');

      const { data: transactionsData, error: transactionsError } = await supabase
        .from('transactions')
        .select('amount')
        .or(`vendor_smart_key.eq.${profileSmartKey},customer_smart_key.eq.${profileSmartKey}`);

      if (transactionsError) throw transactionsError;

      const balance = transactionsData?.reduce((sum, tx) => {
        if (currentUser.account_type === 'customer') {
          return sum + (tx.amount * -1);
        }
        return sum + tx.amount;
      }, 0) || 0;

      setProfile({ ...profileData, balance });
    } catch (err) {
      console.error('Error fetching profile details:', err);
      setError('فشل في تحميل تفاصيل الملف الشخصي');
      navigate('/home');
    } finally {
      setLoading(false);
    }
  };

  const fetchTotalBalance = async () => {
    try {
      if (!profile?.smart_key || !currentUser?.smart_key) return;

      const { data, error } = await supabase
        .from('transactions')
        .select('amount, payment_type')
        .or(
          currentUser.account_type === 'customer'
            ? `and(vendor_smart_key.eq.${profile.smart_key},customer_smart_key.eq.${currentUser.smart_key})`
            : `and(vendor_smart_key.eq.${currentUser.smart_key},customer_smart_key.eq.${profile.smart_key})`
        );

      if (error) throw error;

      const total = (data || []).reduce((sum, transaction) => {
        const amount = Math.abs(transaction.amount);
        if (currentUser.account_type === 'customer') {
          return transaction.payment_type === 'debit' 
            ? sum + amount 
            : sum - amount;
        } else {
          return transaction.payment_type === 'debit'
            ? sum + amount
            : sum - amount;
        }
      }, 0);

      setTotalBalance(total);
    } catch (err) {
      console.error('Error fetching total balance:', err);
    }
  };

  const fetchTransactions = async (currentUser: Profile) => {
    try {
      if (!profile?.smart_key) return;

      setLoading(true);

      let startDate = new Date();
      let query = supabase
        .from('transactions')
        .select(`
          *,
          customer:profiles!transactions_customer_smart_key_fkey(
            username,
            avatar_url
          ),
          vendor:profiles!transactions_vendor_smart_key_fkey(
            username,
            avatar_url
          )
        `)
        .order('created_at', { ascending: false });

      if (timeframe !== 'All') {
        switch (timeframe) {
          case 'Day':
            startDate.setHours(0, 0, 0, 0);
            break;
          case 'Week':
            startDate.setDate(startDate.getDate() - 7);
            break;
          case 'Month':
            startDate.setDate(startDate.getDate() - 30);
            break;
          case 'Year':
            startDate.setDate(startDate.getDate() - 360);
            break;
        }
        query = query.gte('created_at', startDate.toISOString());
      }

      if (currentUser.account_type === 'shop_owner') {
        query = query
          .eq('vendor_smart_key', currentUser.smart_key)
          .eq('customer_smart_key', profileSmartKey);
      } else {
        query = query
          .eq('customer_smart_key', currentUser.smart_key)
          .eq('vendor_smart_key', profileSmartKey);
      }

      const { data, error } = await query;

      if (error) throw error;

      setTransactions(data || []);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('فشل في تحميل المعاملات');
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Math.abs(amount));
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('ar-EG', {
      month: 'short',
      day: 'numeric',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const calculateTotals = () => {
    return transactions.reduce(
      (acc, transaction) => {
        if (transaction.payment_type === 'debit') {
          acc.debit += Math.abs(transaction.amount);
        } else {
          acc.credit += Math.abs(transaction.amount);
        }
        return acc;
      },
      { debit: 0, credit: 0 }
    );
  };

  const calculateRunningBalance = () => {
    let runningBalance = 0;
    return transactions.map(tx => {
      const amount = tx.amount;
      runningBalance += currentUser?.account_type === 'customer' ? -amount : amount;
      return { ...tx, balance: runningBalance };
    });
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white p-8 flex items-center justify-center">
        <div className="text-gray-500">جاري التحميل...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white p-8">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  const totals = calculateTotals();
  const transactionsWithBalance = calculateRunningBalance();

  return (
    <div className="min-h-screen bg-white">
      {/* Print Styles */}
      <style jsx>{`
        @media print {
          @page {
            size: A4;
            margin: 20mm;
          }
          
          .no-print {
            display: none !important;
          }
          
          .print-break {
            page-break-before: always;
          }
          
          body {
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
        }
        
        @media screen {
          .print-only {
            display: none;
          }
        }
      `}</style>

      {/* Screen Controls */}
      <div className="no-print bg-gray-50 p-4 border-b">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => navigate(-1)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              العودة
            </button>
            <div className="flex items-center space-x-2 bg-white rounded-lg p-1">
              {[
                { value: 'Day', label: 'يوم' },
                { value: 'Week', label: 'أسبوع' },
                { value: 'Month', label: 'شهر' },
                { value: 'Year', label: 'سنة' },
                { value: 'All', label: 'الكل' }
              ].map(({ value, label }) => (
                <button
                  key={value}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    timeframe === value 
                      ? 'bg-blue-500 text-white' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  onClick={() => setTimeframe(value as TimeframeType)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={handlePrint}
            className="px-6 py-2 bg-[#F87171] text-white rounded-lg hover:bg-[#EF4444] transition-colors"
          >
            طباعة كشف الحساب
          </button>
        </div>
      </div>

      {/* Printable Content */}
      <div className="max-w-4xl mx-auto p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">كشف حساب</h1>
          <div className="w-24 h-1 bg-[#F87171] mx-auto rounded"></div>
        </div>

        {/* Account Information */}
        <div className="grid grid-cols-2 gap-8 mb-8">
          <div className="space-y-3">
            <div className="flex justify-between border-b pb-2">
              <span className="font-medium text-gray-600">التاريخ:</span>
              <span className="font-semibold">{new Date().toLocaleDateString('ar-EG')}</span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="font-medium text-gray-600">الفترة:</span>
              <span className="font-semibold">
                {timeframe === 'Day' ? 'يوم' : 
                 timeframe === 'Week' ? 'أسبوع' : 
                 timeframe === 'Month' ? 'شهر' : 
                 timeframe === 'Year' ? 'سنة' :
                 'الكل'}
              </span>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between border-b pb-2">
              <span className="font-medium text-gray-600">
                {currentUser?.account_type === 'shop_owner' ? 'العميل' : 'التاجر'}:
              </span>
              <span className="font-semibold">{profile?.username}</span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="font-medium text-gray-600">Smart Key:</span>
              <span className="font-semibold font-mono">{profile?.smart_key}</span>
            </div>
          </div>
        </div>

        {/* Financial Summary */}
        <div className="bg-gray-50 rounded-xl p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">الملخص المالي</h2>
          <div className="grid grid-cols-3 gap-6">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">إجمالي الأجل</p>
              <p className="text-2xl font-bold text-red-600">
                {formatAmount(totals.debit)} {profile?.currency}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">إجمالي الدائن</p>
              <p className="text-2xl font-bold text-green-600">
                {formatAmount(totals.credit)} {profile?.currency}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">الرصيد الحالي</p>
              <p className={`text-2xl font-bold ${
                totalBalance > 0 ? 'text-red-600' : totalBalance < 0 ? 'text-green-600' : 'text-gray-600'
              }`}>
                {formatAmount(Math.abs(totalBalance))} {profile?.currency}
              </p>
            </div>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">تفاصيل المعاملات</h2>
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F87171] text-white">
                  <th className="px-4 py-3 text-right font-semibold">التاريخ</th>
                  <th className="px-4 py-3 text-center font-semibold">مدين</th>
                  <th className="px-4 py-3 text-center font-semibold">دائن</th>
                  <th className="px-4 py-3 text-right font-semibold">البيان</th>
                  <th className="px-4 py-3 text-center font-semibold">الرصيد</th>
                </tr>
              </thead>
              <tbody>
                {transactionsWithBalance.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                      لا توجد معاملات في هذه الفترة
                    </td>
                  </tr>
                ) : (
                  transactionsWithBalance.map((transaction, index) => (
                    <tr key={transaction.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="px-4 py-3 text-sm">
                        {formatDate(transaction.created_at)}
                      </td>
                      <td className="px-4 py-3 text-center text-sm font-mono">
                        {transaction.payment_type === 'debit' ? (
                          <span className="text-red-600 font-semibold">
                            {formatAmount(Math.abs(transaction.amount))}
                          </span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-sm font-mono">
                        {transaction.payment_type === 'credit' ? (
                          <span className="text-green-600 font-semibold">
                            {formatAmount(Math.abs(transaction.amount))}
                          </span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {transaction.remarks || 'بدون ملاحظات'}
                      </td>
                      <td className="px-4 py-3 text-center text-sm font-mono font-semibold">
                        <span className={
                          transaction.balance > 0 ? 'text-red-600' : 
                          transaction.balance < 0 ? 'text-green-600' : 
                          'text-gray-600'
                        }>
                          {formatAmount(Math.abs(transaction.balance))} {transaction.currency}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t pt-6 mt-8">
          <div className="flex justify-between items-center text-sm text-gray-600">
            <div>
              <p>تم إنشاؤه في: {new Date().toLocaleString('ar-EG')}</p>
              <p>بواسطة: تطبيق آجل - Ajel.life</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrintableStatement;